/**
 * Firebase initialization.
 *
 * The app can run in two modes:
 *  - Seed mode (default): `VITE_USE_FIRESTORE=false`. No Firebase project is
 *    required; the UI runs against local seed data. Useful before the project
 *    is provisioned.
 *  - Live mode: `VITE_USE_FIRESTORE=true` with the VITE_FIREBASE_* env vars set.
 *
 * Set `VITE_USE_EMULATORS=true` to point Auth/Firestore/Storage/Functions at
 * the local Firebase emulators.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';

const env = import.meta.env;

export const useFirestore = env.VITE_USE_FIRESTORE === 'true';
const useEmulators = env.VITE_USE_EMULATORS === 'true';

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

const isConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

let app: FirebaseApp | undefined;
let authInstance: Auth | undefined;
let dbInstance: Firestore | undefined;
let storageInstance: FirebaseStorage | undefined;
let functionsInstance: Functions | undefined;

if (isConfigured) {
  app = initializeApp(firebaseConfig);
  authInstance = getAuth(app);
  dbInstance = getFirestore(app);
  storageInstance = getStorage(app);
  functionsInstance = getFunctions(app);

  if (useEmulators) {
    connectAuthEmulator(authInstance, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(dbInstance, '127.0.0.1', 8080);
    connectStorageEmulator(storageInstance, '127.0.0.1', 9199);
    connectFunctionsEmulator(functionsInstance, '127.0.0.1', 5001);
  }
} else if (useFirestore) {
  console.warn(
    '[firebase] VITE_USE_FIRESTORE is true but Firebase config is missing. ' +
      'Falling back to seed data. Fill in .env.local to enable live data.',
  );
}

/** True when a real Firebase project is configured. */
export const isFirebaseConfigured = isConfigured;

export const firebaseApp = app;
export const auth = authInstance;
export const db = dbInstance;
export const storage = storageInstance;
export const functions = functionsInstance;
